import { normalize } from 'normalizr';
import { TilesService } from '@/services/tiles.service.js';
import { find } from 'lodash';
import {
  tileSchema,
  tilesGroupSchema,
  tilesTypeSchema,
  TILES_GROUP
} from './schema';
import { get, patch, post, remove } from '@/helpers/httpHandler';

const nestedRemoveById = function nestedRemoveById(
  list,
  id,
  prop = 'children'
) {
  return list.filter(item => {
    if (item[prop]) {
      item[prop] = nestedRemoveById(item[prop], id);
    }

    return item.id !== id;
  });
};

export default {
  setDefaultTilesGroup({ commit }, entities) {
    const tilesGroup = find(entities[TILES_GROUP], i => Boolean(i.default));

    if (!tilesGroup) {
      return;
    }

    commit('setDefaultTilesGroup', tilesGroup.id);
  },

  setTilesGroup({ commit, dispatch }, { tilesGroup }) {
    const { entities, result } = normalize(tilesGroup, tilesGroupSchema);

    commit('setEntities', entities);
    commit('addTileGroup', result);
    dispatch('setDefaultTilesGroup', entities);
  },

  getTilesGroups({ commit, dispatch }, { applicationId }) {
    return TilesService.getTilesGroupsById(applicationId).then(response => {
      const { entities, result } = normalize(response.data.data, [
        tilesGroupSchema
      ]);

      commit('setEntities', entities);
      commit('setTilesGroups', result);
      dispatch('setDefaultTilesGroup', entities);
      return response;
    });
  },

  getTilesGroup: ({ dispatch }, { tilesGroupId }) => {
    return get(`/applications/tiles-groups/${tilesGroupId}`).then(response => {
      dispatch('setTilesGroup', { tilesGroup: response.data });

      return response;
    });
  },

  cloneTilesGroup: ({ dispatch }, { tilesGroupId }) => {
    return post(`/applications/tiles-groups/clone/${tilesGroupId}`).then(response => {
      dispatch('setTilesGroup', { tilesGroup: response.data });

      return response;
    });
  },

  storeTilesGroup: ({ dispatch }, { applicationId, tilesGroup }) => {
    return post(`/applications/${applicationId}/tiles-group`, tilesGroup).then(
      response => {
        dispatch('setTilesGroup', { tilesGroup: response.data });

        return response;
      }
    );
  },

  updateTilesGroup: ({ dispatch }, { tilesGroupId, tilesGroup }) => {
    return patch(`/applications/tiles-groups/${tilesGroupId}`, tilesGroup).then(
      response => {
        dispatch('setTilesGroup', { tilesGroup: response.data });

        return response;
      }
    );
  },

  getTiles: ({ commit }, { tilesGroupId }) => {
    return get(`/applications/${tilesGroupId}/tiles`).then(response => {
      const { entities, result } = normalize(response.data.data, [tileSchema]);

      commit('setEntities', entities);
      commit('setTiles', result);

      return response;
    });
  },

  setTile: ({ commit }, { tile }) => {
    const { entities, result } = normalize(tile, tileSchema);

    commit('setEntities', entities);
    commit('addTile', result);
  },

  getTile: ({ dispatch }, { tileId }) => {
    return get(`/applications/tiles/${tileId}`).then(response => {
      const tile = { ...response.data };
      delete tile.children; // Update only tile

      dispatch('setTile', { tile });

      return response;
    });
  },

  storeTile: ({ dispatch }, { tilesGroupId, tile }) => {
    return post(`/applications/${tilesGroupId}/tile`, tile).then(response => {
      dispatch('setTile', { tile: response.data });

      return response;
    });
  },

  updateTile: ({ dispatch }, { tileId, tile }) => {
    return patch(`/applications/tiles/${tileId}`, tile).then(response => {
      const tile = { ...response.data };
      delete tile.children; // Update only tile

      dispatch('setTile', { tile });

      return response;
    });
  },

  destroyTile: ({ dispatch, commit, getters }, { tilesGroupId, tileId }) => {
    remove(`/applications/tiles/${tileId}`).then(response => {
      const tilesGroup = getters.GET_TILES_GROUP_BY_ID(tilesGroupId);

      tilesGroup.tiles = nestedRemoveById(tilesGroup.tiles, tileId);
      commit('removeTile', tileId);
      dispatch('setTilesGroup', { tilesGroup });

      return response;
    });
  },

  getTilesTypes: ({ commit }) => {
    return get('/applications/tiles-types/all').then(response => {
      const { entities, result } = normalize(response.data.data, [
        tilesTypeSchema
      ]);

      commit('setEntities', entities);
      commit('setTilesTypes', result);

      return response;
    });
  },

  getTilesContents: (_, { tileTypeId }) =>
    get(`/applications/tiles/filters/${tileTypeId}`)
};
